import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { StatusBadge, MetricsDisplay } from '@/components/scan'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import type { Scan } from '@/lib/types'
import { formatDateTime } from '@/lib/utils/timeFormat'

interface ScanInfoCardProps {
  scan: Scan
  className?: string
}

export function ScanInfoCard({ scan, className }: ScanInfoCardProps) {
  const { activeCrisis } = useCrisisStore()

  // Calculate unique sources count (not including duplicates)
  let uniqueSourcesCount = 0
  if (scan.sources) {
    if (Array.isArray(scan.sources)) {
      uniqueSourcesCount = scan.sources.length
    } else if (typeof scan.sources === 'object') {
      uniqueSourcesCount = Object.keys(scan.sources).length
    }
  }

  return (
    <Card className={className + ' mb-6'}>
      <CardHeader>
        <CardTitle className="text-base flex">
          <span className="border-r-1 border-black pe-6 me-6">Overzicht</span>
          <MetricsDisplay totalMessages={uniqueSourcesCount} size="lg" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-foreground">
              {scan.title} - {activeCrisis?.location}
            </h1>
            <StatusBadge status={scan.status} />
          </div>

          {activeCrisis?.description && (
            <p className="text-sm text-muted-foreground mb-3">{activeCrisis.description}</p>
          )}

          <div className="space-y-3 text-sm">
            {scan.createdBy && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground font-medium min-w-[60px]">Door:</span>
                <span className="text-foreground">
                  {scan.createdBy.displayName ||
                    `${scan.createdBy.firstName} ${scan.createdBy.lastName}`}
                </span>
              </div>
            )}
            {scan.timeFrame.from && scan.timeFrame.to && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground font-medium min-w-[60px]">Van:</span>
                  <span className="text-foreground">{formatDateTime(scan.timeFrame.from)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground font-medium min-w-[60px]">Tot:</span>
                  <span className="text-foreground">{formatDateTime(scan.timeFrame.to)}</span>
                </div>
              </>
            )}
            {(scan.searchQuery || (scan.keywords && scan.keywords.length > 0)) && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground font-medium min-w-[60px]">Zoekterm:</span>
                <code className="text-xs font-mono text-foreground bg-muted/50 px-2 py-1 rounded border border-border">
                  {scan.searchQuery || scan.keywords.length === 0 ? (
                    scan.searchQuery || `(${scan.location})`
                  ) : (
                    <>
                      ({scan.location}) AND ({scan.keywords.slice(0).join(' OR ')})
                    </>
                  )}
                </code>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
